import { useEffect } from 'react';
import { X } from 'lucide-react';

interface SortedLineOption {
  id: string;
  title: string;
  artist: string;
}

interface SortedLinePickerProps {
  order: SortedLineOption[];
  rankSize: number;
  currentId: string | null;
  onPick: (id: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

export function SortedLinePicker({ order, rankSize, currentId, onPick, onRemove, onClose }: SortedLinePickerProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="sorted-line-picker"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sorted-line-title"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 id="sorted-line-title" className="text-base font-semibold text-gray-900 dark:text-white">
              Put the line below…
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Mark how far you’ve sorted. It’s just for you and doesn’t change your ranking.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-[8rem]">
          {order.map((option, index) => {
            const position = index + 1;
            const isCurrent = option.id === currentId;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onPick(option.id)}
                aria-current={isCurrent}
                className={`w-full text-left p-2 rounded-lg transition-colors flex items-center gap-2 ${
                  isCurrent ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className={`min-w-[2rem] text-center text-sm font-bold ${position <= rankSize ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>
                  #{position}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{option.title}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{option.artist}</span>
                </span>
                {isCurrent && <span className="text-xs font-medium text-teal-600 dark:text-teal-400">Line is here</span>}
              </button>
            );
          })}
        </div>

        {currentId && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onRemove} className="btn btn-secondary w-full text-sm">
              Remove the line
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
